<script setup lang="ts">
import {QTabPanel, useDialogPluginComponent} from 'quasar'
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import Translator from "@/utils/common/Translator.ts";
import DialogButtons from "@/utils/forms/DialogButtons.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {onBeforeMount, Ref, ref} from "vue";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import EditViewSkeleton from "@/components/skeleton/EditViewSkeleton.vue";
import EditViewBlock from "@/components/EditView/EditViewBlock.vue";
import ViewMode from "@/utils/constants/ViewMode.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import EditScreenLayout = namaTypes.EditScreenLayout;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import NaMaMetaData = namaTypes.NaMaMetaData;

const props = defineProps<{
  object: Record<string, any>,
  editLayout: EditScreenLayout,
  metaData: NaMaMetaData,
  entityType: string,
  title: string
}>();
const {compactMode, darkMode} = storeToRefs(useNamaFlagsStore());
const currentPageIndex: Ref<number> = ref(0);
const object: Ref<BaseEntityDTO | undefined> = ref(props.object);
const place: EditPlace = new EditPlace();
place.view = ViewMode.EDIT;
place.quickCreate = true;
const presenter = new EditPresenter(place);
presenter.metadata = props.metaData;
presenter.layout = props.editLayout;
presenter.createdObject = object.value;
if (presenter.createdObject)
  presenter.createdObject.revised = true;
onBeforeMount(async () => {
  await presenter.init();
});

const title: string = Translator.translateId('showGeneratedSystemTransaction') + ' - ' + Translator.translateId(props.entityType);

const {dialogRef, onDialogCancel} = useDialogPluginComponent()
const loginStore = useLoginInfoStore();
const closeDialog = onDialogCancel;


function onPageChanged(newValue: number) {
  currentPageIndex.value = newValue;
}
</script>

<template>
  <q-dialog persistent ref="dialogRef" @hide="closeDialog" v-if="loginStore.isLoggedIn" full-height full-width @keydown.esc="closeDialog">
    <q-card class="tw-flex tw-flex-col tw-w-full tw-h-full tw-shadow-none tw-bg-namaBackground-bright-light dark:tw-text-white dark:tw-bg-namaBackground-bright-dark">
      <q-bar
        class="tw-flex-none tw-h-fit tw-min-h-8 tw-text-namaFontColor-input-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] tw-flex tw-justify-between dark:tw-bg-[#212529] dark:tw-text-white">
        <div>{{ title }}</div>
        <q-btn dense flat @click="closeDialog" :title="Translator.translateId( 'close')" class="tw-h-fit">
          <NamaIcon default-icon="nm-win-close"/>
        </q-btn>
      </q-bar>
      <q-card-section class="tw-grow tw-flex tw-flex-col compact:tw-p-0 ultra:tw-p-0">
        <div class="tw-grow tw-bg-namaBackground-bright-light tw-rounded dark:tw-bg-namaBackground-bright-dark">
          <div class="tw-h-full tw-flex tw-flex-col compact:tw-pt-2 ultra:tw-pt-1 compact:tw-p-3 ultra:tw-p-1.5">
            <template v-if="presenter.validLayout">
              <div class="tw-flex-none tw-h-fit tw-top-0 tw-rounded-b-lg tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-pb-0 compact:tw-px-0.5 ultra:tw-px-0">
                <q-tabs
                  :model-value="currentPageIndex"
                  @update:model-value="onPageChanged"
                  content-class="edit-view-tab-content tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark tw-pt-2 tw-px-2.5 compact:tw-px-1 ultra:tw-px-0.5 mobile:tw-px-2.5"
                  indicator-color="primary"
                  align="left"
                  :dense="compactMode"
                  narrow-indicator
                  :no-caps="true"
                >
                  <q-separator v-if="!compactMode"/>
                  <q-tab
                    v-for="(page, pageIndex) in presenter.pages.value" :key="pageIndex"
                    :name="pageIndex" :label="Translator.translateNaMaText(page.title, presenter.entityType)"/>
                </q-tabs>
              </div>
              <q-tab-panels keep-alive animated v-model="currentPageIndex"
                            class="tw-grow tw-bg-namaBackground-bright-light tw-rounded-lg dark:tw-bg-namaBackground-bright-dark">
                <q-tab-panel ref="qTabPanelRef" v-for="(page, pageIndex) in presenter.pages.value" :key="pageIndex" :name="pageIndex"
                             class="tw-h-full tw-p-0 tw-pt-4 compact:tw-p-0 ultra:tw-p-0 compact:tw-pt-1 ultra:tw-pt-0.5">
                  <q-scroll-area :dir="Translator.LTR" :dark="darkMode" class="tw-h-full">
                    <div :dir="Translator.fetchDir()" class="tw-flex tw-flex-wrap tw-gap-2.5 compact:tw-gap-1 ultra:tw-gap-0.5">
                      <template v-for="(block, blockIndex) in page.blocks">
                        <EditViewBlock :block="block" :presenter="presenter" :blockIndex="blockIndex"
                                       :pageIndex="pageIndex" :showMainFile="false"/>
                      </template>
                    </div>
                  </q-scroll-area>
                </q-tab-panel>
              </q-tab-panels>
            </template>
            <template v-else>
              <EditViewSkeleton/>
            </template>
          </div>
        </div>
      </q-card-section>
      <q-card-section class="tw-flex-none tw-h-fit tw-p-0 tw-pb-4 compact:tw-pb-2 ultra:tw-pb-1 tw-flex tw-flex-row tw-gap-4 tw-justify-center">
        <q-btn
          outline
          :label="Translator.translateId(DialogButtons.Close)"
          @click="closeDialog"
          :dense="compactMode"
          class="tw-h-10 tw-text-namaGray dark:tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-28 tw-max-w-fit"/>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>

</style>
